import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Upload, AlertCircle } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { importReportsCsv } from '../../../services/reportService';

/**
 * Parse CSV text into rows (simple split — handles basic cases).
 * Returns array of arrays (each inner array = one row of cell values).
 */
function parseCsvPreview(text, maxRows = 5) {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  const rows = [];
  for (let i = 0; i < Math.min(lines.length, maxRows + 1); i++) {
    // Simple comma split — good enough for preview
    rows.push(lines[i].split(',').map(c => c.replace(/^"|"$/g, '').trim()));
  }
  return rows; // first row = headers, rest = data
}

export default function ImportCsvModal({ isOpen, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setError(null);
    setValidationErrors(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    setError(null);
    setValidationErrors(null);
    const selected = e.target.files?.[0];
    if (!selected) { setFile(null); setPreview(null); return; }

    setFile(selected);

    try {
      const text = await selected.text();
      const rows = parseCsvPreview(text, 5);
      setPreview(rows);
    } catch {
      setPreview(null);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setValidationErrors(null);

    try {
      const result = await importReportsCsv(file);
      reset();
      onSuccess(result);
      onClose();
    } catch (err) {
      if (err.errorCode === 'CSV_VALIDATION_ERROR' && err.details?.errors) {
        setValidationErrors(err.details.errors);
        if (err.details.error_count > err.details.errors_shown) {
          setError(`Showing ${err.details.errors_shown} of ${err.details.error_count} errors. Fix these first, then re-upload.`);
        }
      } else {
        setError(err.message || 'Import failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
      <Button variant="secondary" onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={!file || loading}
        loading={loading}
        icon={<Upload size={18} />}
      >
        {loading ? 'Importing…' : 'Import'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Reports from CSV"
      size="lg"
      footer={footer}
      closeOnBackdrop={!loading}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* File input */}
        <div>
          <label
            htmlFor="csv-file-input"
            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: 'var(--text-primary, #e0e0e0)' }}
          >
            Select CSV file
          </label>
          <input
            id="csv-file-input"
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid var(--border-color, #444)',
              borderRadius: '0.375rem',
              background: 'var(--bg-secondary, #1e1e2e)',
              color: 'var(--text-primary, #e0e0e0)'
            }}
          />
        </div>

        {/* CSV Preview */}
        {preview && preview.length > 1 && (
          <div>
            <h4 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary, #e0e0e0)' }}>
              Preview (first {Math.min(preview.length - 1, 5)} rows)
            </h4>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border-color, #444)', borderRadius: '0.375rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    {preview[0].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: '0.5rem 0.75rem',
                          textAlign: 'left',
                          background: 'var(--bg-tertiary, #2a2a3e)',
                          color: 'var(--text-primary, #e0e0e0)',
                          borderBottom: '1px solid var(--border-color, #444)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(1).map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderBottom: '1px solid var(--border-color, #333)',
                            color: 'var(--text-secondary, #b0b0b0)',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* General error */}
        {error && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '0.375rem',
              color: '#ef4444'
            }}
          >
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Validation errors table */}
        {validationErrors && validationErrors.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
                color: '#ef4444',
                fontWeight: 500
              }}
            >
              <AlertCircle size={18} />
              <span>{validationErrors.length} validation error{validationErrors.length !== 1 ? 's' : ''} found</span>
            </div>
            <div
              style={{
                maxHeight: '250px',
                overflowY: 'auto',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.375rem'
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', whiteSpace: 'nowrap' }}>Row</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', whiteSpace: 'nowrap' }}>Field</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderBottom: '1px solid rgba(239, 68, 68, 0.3)' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {validationErrors.map((ve, i) => (
                    <tr key={i}>
                      <td style={{ padding: '0.4rem 0.75rem', borderBottom: '1px solid var(--border-color, #333)', color: 'var(--text-secondary, #b0b0b0)' }}>{ve.row}</td>
                      <td style={{ padding: '0.4rem 0.75rem', borderBottom: '1px solid var(--border-color, #333)', color: 'var(--text-secondary, #b0b0b0)', fontFamily: 'monospace' }}>{ve.field}</td>
                      <td style={{ padding: '0.4rem 0.75rem', borderBottom: '1px solid var(--border-color, #333)', color: 'var(--text-secondary, #b0b0b0)' }}>{ve.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

ImportCsvModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};
